/* eslint-disable react-refresh/only-export-components */

import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	DEFAULT_APPLICATION,
	useApplicationManagement
} from '@/features/Application/hooks/useApplicationManagement';
import { usePremiumVarManagement } from '@/features/Quote/hooks/usePremiumVarManagement';
import type {
	AppData,
	ApplicationData,
	ConfigData,
	PendingPremiumVars,
	SettingType
} from '@/types/app';

export const HIDE_HEADER_STORAGE_KEY = 'quotr_hide_header';

/**
 * Configuration object containing the current app configuration
 */
interface FnlConfig {
	/** Current configuration data for the application */
	currentConfig: ConfigData;
}

/**
 * Props for the AppContextProvider component
 */
interface AppContextProviderProps {
	/** Child components to be wrapped by the context provider */
	children: React.ReactNode;
	/** Configuration object containing app-specific settings */
	fnlConfig: FnlConfig;
}

/**
 * Type definition for the application context
 * Contains all global state and management functions for the Quotr application
 */
interface AppContextType {
	/** Current application data including policy and quote information */
	appData: AppData | null;
	/** Function to update the application data */
	setAppData: React.Dispatch<React.SetStateAction<AppData | null>>;
	/** Current application form data (user inputs, validation state) */
	applicationData: ApplicationData;
	/** Function to update the application form data */
	setApplicationData: React.Dispatch<React.SetStateAction<ApplicationData>>;
	/** Whether the application is currently in a loading state */
	isLoading: boolean;
	/** Function to update the loading state */
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	/** Whether quotes are currently being fetched from insurers */
	isFetchingQuotes: boolean;
	/** Function to update the quote fetching state */
	setIsFetchingQuotes: React.Dispatch<React.SetStateAction<boolean>>;
	/** Whether a form submission is currently in progress */
	isSubmitting: boolean;
	/** Function to update the submission state */
	setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
	/** Current error message, if any */
	isError: string | null;
	/** Function to update the error state */
	setIsError: React.Dispatch<React.SetStateAction<string | null>>;
	/** Current application configuration */
	config: ConfigData;
	/** Pending premium variable changes that haven't been saved yet */
	pendingPremiumVars: PendingPremiumVars;
	/** Function to update pending premium variables */
	setPendingPremiumVars: React.Dispatch<React.SetStateAction<PendingPremiumVars>>;
	/** Premium variable management functions (employees, absence, coverage) */
	premiumVarManagement: ReturnType<typeof usePremiumVarManagement>;
	/** Application form management functions (validation, submission) */
	applicationManagement: ReturnType<typeof useApplicationManagement>;
	/** Whether to hide the header (for iframe embedding) */
	hideHeader: boolean;
	/**
	 * Whether the mobile "your details" side panel is open. On desktop the sidebar
	 * is always visible; below xl it collapses into a slide-over opened from the
	 * header hamburger. Shared here because the trigger lives in the (global) Header
	 * while the drawer content is rendered by the Quote page.
	 */
	isMobileSidebarOpen: boolean;
	/** Open/close the mobile side panel. */
	setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/**
	 * Steps the user has confirmed during the current confirmation-wizard session,
	 * before the deferred recalculation persists their new (trusted) source. Shared in
	 * context so the settings cards (green check) and the top guidance bar agree on which
	 * step is still outstanding — a step is "done" when trusted OR confirmed this session.
	 */
	confirmedSteps: ReadonlySet<SettingType>;
	/** Optimistically mark a wizard step confirmed for the current session. */
	markStepConfirmed: (step: SettingType) => void;
	/**
	 * Drop the given steps from the confirmed-session set (after their deferred commit
	 * succeeds). Only the committed steps are removed, not the whole set, so a step
	 * confirmed while an earlier commit was still in flight survives to be committed next.
	 */
	clearConfirmedSteps: (steps: Iterable<SettingType>) => void;
}

/**
 * React context for sharing application state across components
 * Provides access to global state, configuration, and management functions
 */
export const AppContext = createContext<AppContextType | null>(null);

/**
 * Provider component that wraps the application with context
 * Manages global state for the Quotr insurance application including:
 * - Application data (policy, quotes, employees)
 * - Form state and validation
 * - Loading states for async operations
 * - Error handling
 * - Premium variable management
 * - Application form management
 *
 * @param props - Component props
 * @param props.children - Child components to be wrapped
 * @param props.fnlConfig - Configuration object with app-specific settings
 * @returns JSX element providing context to children
 */
export function AppContextProvider({
	children,
	fnlConfig
}: AppContextProviderProps): React.JSX.Element {
	const [appData, setAppData] = useState<AppData | null>(null);
	const [applicationData, setApplicationData] = useState<ApplicationData>(
		DEFAULT_APPLICATION as ApplicationData
	);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isFetchingQuotes, setIsFetchingQuotes] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isError, setIsError] = useState<string | null>(null);
	const [config] = useState<ConfigData>(fnlConfig.currentConfig);
	const [pendingPremiumVars, setPendingPremiumVars] = useState<PendingPremiumVars>({
		employeesTotalWage: null,
		employeeCount: null,
		absenceValues: {},
		coverageValues: null
	});

	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);

	const [confirmedSteps, setConfirmedSteps] = useState<ReadonlySet<SettingType>>(new Set());
	const markStepConfirmed = useCallback((step: SettingType) => {
		setConfirmedSteps((prev) => new Set(prev).add(step));
	}, []);
	const clearConfirmedSteps = useCallback((steps: Iterable<SettingType>) => {
		setConfirmedSteps((prev) => {
			const next = new Set(prev);
			for (const step of steps) next.delete(step);
			return next;
		});
	}, []);

	const premiumVarManagement = usePremiumVarManagement(setAppData, setPendingPremiumVars);
	const applicationManagement = useApplicationManagement(setApplicationData);

	// Parse URL param for hideHeader (read-only, no side effects)
	const hideHeaderUrlValue = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		return params.get('hideHeader');
	}, []);

	// Compute hideHeader from URL params or sessionStorage (pure computation)
	const hideHeader = useMemo(() => {
		// URL param takes precedence
		if (hideHeaderUrlValue === 'true' || hideHeaderUrlValue === '1') {
			return true;
		}
		if (hideHeaderUrlValue === 'false' || hideHeaderUrlValue === '0') {
			return false;
		}

		// Fall back to sessionStorage (survives hard redirects)
		try {
			return sessionStorage.getItem(HIDE_HEADER_STORAGE_KEY) === 'true';
		} catch {
			// Default to false on storage access error
			return false;
		}
	}, [hideHeaderUrlValue]);

	// Persist hideHeader to sessionStorage (side effect)
	useEffect(() => {
		try {
			if (hideHeaderUrlValue === 'true' || hideHeaderUrlValue === '1') {
				sessionStorage.setItem(HIDE_HEADER_STORAGE_KEY, 'true');
			} else if (hideHeaderUrlValue === 'false' || hideHeaderUrlValue === '0') {
				sessionStorage.removeItem(HIDE_HEADER_STORAGE_KEY);
			}
		} catch {
			// Ignore SecurityError (e.g., blocked storage access)
		}
	}, [hideHeaderUrlValue]);

	const value: AppContextType = {
		appData,
		setAppData,
		applicationData,
		setApplicationData,
		isLoading,
		setIsLoading,
		isFetchingQuotes,
		setIsFetchingQuotes,
		isSubmitting,
		setIsSubmitting,
		isError,
		setIsError,
		config,
		pendingPremiumVars,
		setPendingPremiumVars,
		premiumVarManagement,
		applicationManagement,
		hideHeader,
		isMobileSidebarOpen,
		setIsMobileSidebarOpen,
		confirmedSteps,
		markStepConfirmed,
		clearConfirmedSteps
	};

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * Custom hook to access the application context
 * Provides type-safe access to all global state and management functions
 *
 * @throws {Error} When used outside of AppContextProvider
 * @returns The application context with all state and management functions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { appData, isLoading, premiumVarManagement } = useAppContext();
 *
 *   if (isLoading) return <Loader />;
 *
 *   const handleSaveEmployees = async () => {
 *     const id = appData?.policy_upload_form_id;
 *     const employees = appData?.premium_variables?.employees?.employee_list || [];
 *     if (id) {
 *       await premiumVarManagement.employees.save(id, employees);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <h1>{appData?.company_name}</h1>
 *       <button onClick={handleSaveEmployees}>{t('common.saveEmployees')}</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAppContext(): AppContextType {
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('useAppContext must be used within an AppContextProvider');
	}
	return context;
}
